// shop/delivery-ui.js — блок доставки на шаге оформления
// =============================================================================
// Хуки разметки:
//   [data-delivery]                 корневой блок
//   [data-delivery-method]          кнопки способа
//   [data-delivery-city]            поле города
//   [data-delivery-city-suggest]    выпадающий список подсказок
//   [data-delivery-pvz-pick]        «Выбрать пункт выдачи»
//   [data-delivery-pvz-list]        список ПВЗ
//   [data-delivery-pvz-chosen]      карточка выбранного ПВЗ
//   [data-delivery-address-wrap]    поле адреса (курьер)
//   [data-delivery-dates]           <select> дат
//   [data-delivery-cost]            стоимость
//   [data-delivery-note]            пояснение / ошибка

import * as cart from './cart.js';
import * as delivery from './delivery.js';

const money = cart.formatPrice;

function q(sel, root) { return (root || document).querySelector(sel); }
function all(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

function el(tag, options, children) {
  const node = document.createElement(tag);
  const opts = options || {};
  if (opts.class) node.className = opts.class;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.attrs) Object.keys(opts.attrs).forEach(k => node.setAttribute(k, opts.attrs[k]));
  (children || []).forEach(c => node.appendChild(c));
  return node;
}

let onChange = () => {};
let quoteToken = 0;
let suggestTimer = null;

// ── Расчёт ──────────────────────────────────────────────────────────────────
async function recalc() {
  const token = ++quoteToken;
  const method = cart.state().meta.delivery;
  const ship = cart.shipping();

  if (method !== 'pickup' && ship.cityCode) setNote('Считаем стоимость…');

  const result = await delivery.quote({
    method,
    cityCode: ship.cityCode,
    weight: cart.weight(),
  });

  // Медленный ответ по прежнему городу не должен перезаписать новый расчёт.
  if (token !== quoteToken) return;

  if (!result.ok) {
    cart.setShipping({ cost: 0, minDays: 0, maxDays: 0, quoteError: result.reason, test: false });
    onChange();
    return;
  }

  const dates = delivery.deliveryDates(result.minDays, result.maxDays).map(delivery.isoDate);
  const keepDate = dates.includes(ship.date) ? ship.date : (dates[0] || '');

  cart.setShipping({
    cost: result.cost,
    minDays: result.minDays,
    maxDays: result.maxDays,
    test: Boolean(result.test),
    quoteError: '',
    date: result.pickup ? '' : keepDate,
  });

  onChange();
}

function setNote(text) {
  const note = q('[data-delivery-note]');
  if (note) note.textContent = text;
}

// ── Подсказки городов ───────────────────────────────────────────────────────
async function suggestCities(value) {
  const host = q('[data-delivery-city-suggest]');
  if (!host) return;

  const list = await delivery.cities(value).catch(() => []);
  if (!list.length) {
    host.replaceChildren();
    host.hidden = true;
    return;
  }

  host.hidden = false;
  host.replaceChildren(...list.map(c =>
    el('button', {
      class: 'crb-partial-sm-delivery-suggest-item',
      attrs: { type: 'button', 'data-city-code': c.code, 'data-city-name': c.city },
    }, [
      el('span', { class: 'crb-partial-sm-delivery-suggest-city', text: c.city }),
      el('span', { class: 'crb-partial-sm-delivery-suggest-region', text: c.region || '' }),
    ])
  ));
}

function chooseCity(code, name) {
  const input = q('[data-delivery-city]');
  if (input) input.value = name;

  const suggest = q('[data-delivery-city-suggest]');
  if (suggest) suggest.hidden = true;

  // Город сменился — прежний пункт выдачи к нему не относится.
  cart.setShipping({ city: name, cityCode: Number(code), pvz: null });
  clearPvzList();
  recalc();
}

// ── Пункты выдачи ───────────────────────────────────────────────────────────
function clearPvzList() {
  const host = q('[data-delivery-pvz-list]');
  if (host) { host.replaceChildren(); host.hidden = true; host._points = null; }
}

function pvzCaption(result, city, street) {
  if (result.basis === 'distance') return 'Ближайшие к адресу «' + street + '»';
  if (result.basis === 'address') return 'Рядом с «' + street + '» — из ' + result.total + ' пунктов города';
  if (result.noMatch) return 'По адресу «' + street + '» ничего не нашлось. Первые пункты в городе ' + city + ':';
  return result.total > result.items.length
    ? 'Пунктов в городе ' + city + ': ' + result.total + '. Укажите улицу, чтобы увидеть ближайшие.'
    : 'Пункты выдачи в городе ' + city;
}

async function openPvzList() {
  const host = q('[data-delivery-pvz-list]');
  if (!host) return;

  const ship = cart.shipping();
  if (!ship.cityCode) {
    host.hidden = false;
    host.replaceChildren(el('p', { class: 'crb-partial-sm-delivery-loading', text: 'Сначала выберите город из подсказок.' }));
    return;
  }

  const streetInput = q('[data-delivery-street]');
  const street = streetInput ? streetInput.value.trim() : '';

  host.hidden = false;
  host.replaceChildren(el('p', { class: 'crb-partial-sm-delivery-loading', text: 'Ищем пункты выдачи…' }));

  let result;
  try {
    result = await delivery.pickupPoints(ship.cityCode, { city: ship.city, street, limit: 5 });
  } catch (e) {
    host.replaceChildren(el('p', { class: 'crb-partial-sm-delivery-loading', text: 'Не удалось получить список пунктов. Попробуйте ещё раз.' }));
    return;
  }

  const points = result.items || [];
  if (!points.length) {
    host.replaceChildren(el('p', { class: 'crb-partial-sm-delivery-loading', text: 'В этом городе нет пунктов выдачи СДЭК — выберите курьера.' }));
    return;
  }

  host._points = points;
  host.replaceChildren(
    el('p', { class: 'crb-partial-sm-delivery-loading', text: pvzCaption(result, ship.city, street) }),
    ...points.map(p =>
      el('button', {
        class: 'crb-partial-sm-delivery-pvz-option',
        attrs: { type: 'button', 'data-pvz-code': p.code },
      }, [
        el('span', { class: 'crb-partial-sm-delivery-pvz-name', text: p.name || p.code }),
        el('span', { class: 'crb-partial-sm-delivery-pvz-addr', text: p.address }),
        el('span', {
          class: 'crb-partial-sm-delivery-pvz-work',
          text: (p.distanceKm != null ? p.distanceKm + ' км · ' : '') + (p.work || ''),
        }),
      ])
    )
  );
}

function choosePvz(code) {
  const host = q('[data-delivery-pvz-list]');
  const points = (host && host._points) || [];
  const pvz = points.find(p => String(p.code) === String(code));
  if (!pvz) return;

  cart.setShipping({ pvz });
  if (host) host.hidden = true;
  onChange();
}

// ── Отрисовка ───────────────────────────────────────────────────────────────
function renderMethods() {
  const group = q('[data-delivery-method]');
  if (!group) return;
  const current = cart.state().meta.delivery;
  all('[data-meta-value]', group).forEach(btn => {
    const active = btn.getAttribute('data-meta-value') === current;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
  });
}

function renderFields() {
  const method = cart.state().meta.delivery;
  const spec = delivery.METHODS[method] || delivery.METHODS.pickup;
  const ship = cart.shipping();

  const cityInput = q('[data-delivery-city]');
  if (cityInput && document.activeElement !== cityInput) {
    const stored = ship.city || '';
    if (cityInput.value !== stored) cityInput.value = stored;
  }

  const toggle = (sel, show) => { const n = q(sel); if (n) n.hidden = !show; };
  toggle('[data-delivery-city-wrap]', spec.needsCity);
  toggle('[data-delivery-pvz-wrap]', spec.needsPvz);
  // Поиск по улице показываем только когда город выбран: без кода города
  // искать не в чем.
  toggle('[data-delivery-street-wrap]', spec.needsPvz && Boolean(ship.cityCode));
  toggle('[data-delivery-address-wrap]', spec.needsAddress);
  toggle('[data-delivery-dates-wrap]', method !== 'pickup' && Boolean(ship.cityCode) && !ship.quoteError);
}

function renderChosenPvz() {
  const host = q('[data-delivery-pvz-chosen]');
  if (!host) return;
  const pvz = cart.shipping().pvz;

  if (!pvz) { host.replaceChildren(); host.hidden = true; return; }

  host.hidden = false;
  host.replaceChildren(
    el('p', { class: 'crb-partial-sm-delivery-pvz-name', text: pvz.name || pvz.code }),
    el('p', { class: 'crb-partial-sm-delivery-pvz-addr', text: pvz.address }),
    el('p', { class: 'crb-partial-sm-delivery-pvz-work', text: pvz.work || '' })
  );
}

function renderDates() {
  const select = q('[data-delivery-dates]');
  if (!select) return;

  const ship = cart.shipping();
  const dates = delivery.deliveryDates(ship.minDays, ship.maxDays);

  select.replaceChildren(...dates.map(d => {
    const iso = delivery.isoDate(d);
    const opt = el('option', { text: delivery.formatDate(d), attrs: { value: iso } });
    if (iso === ship.date) opt.selected = true;
    return opt;
  }));
}

function renderCost() {
  const t = cart.totals();
  const ship = cart.shipping();
  const method = cart.state().meta.delivery;

  all('[data-delivery-cost]').forEach(node => {
    if (method !== 'pickup' && ship.quoteError) {
      node.textContent = '—';
      node.classList.remove('is-free');
      return;
    }
    node.textContent = t.delivery === 0 ? 'бесплатно' : money(t.delivery);
    node.classList.toggle('is-free', t.delivery === 0);
  });

  if (method === 'pickup') {
    setNote('Заберёте в мастерской — напишу, когда изделие будет готово.');
    return;
  }
  if (ship.quoteError === 'no-city') {
    setNote('Начните вводить город и выберите его из подсказок — СДЭК посчитает доставку.');
    return;
  }
  if (ship.quoteError === 'carrier') {
    setNote('СДЭК сейчас не отвечает. Обновите страницу или напишите мне — посчитаю вручную.');
    return;
  }

  const term = ship.minDays === ship.maxDays
    ? 'срок ' + ship.maxDays + ' дн'
    : 'срок ' + ship.minDays + '–' + ship.maxDays + ' дн';

  setNote('Рассчитано СДЭК: ' + term + '.' + (ship.test ? ' Тестовый контур СДЭК — тарифы не боевые.' : ''));
}

export function render() {
  renderMethods();
  renderFields();
  renderChosenPvz();
  renderDates();
  renderCost();
}

// ── Инициализация ───────────────────────────────────────────────────────────
export function init(notify) {
  const root = q('[data-delivery]');
  if (!root) return;
  onChange = notify || (() => {});

  root.addEventListener('click', e => {
    const method = e.target.closest('[data-delivery-method] [data-meta-value]');
    if (method) {
      cart.setMeta('delivery', method.getAttribute('data-meta-value'));
      recalc();
      return;
    }

    const city = e.target.closest('[data-city-code]');
    if (city) {
      chooseCity(city.getAttribute('data-city-code'), city.getAttribute('data-city-name'));
      return;
    }

    if (e.target.closest('[data-delivery-pvz-pick]')) { openPvzList(); return; }

    const option = e.target.closest('[data-pvz-code]');
    if (option) choosePvz(option.getAttribute('data-pvz-code'));
  });

  const cityInput = q('[data-delivery-city]');
  if (cityInput) {
    cityInput.value = cart.shipping().city || '';
    cityInput.setAttribute('autocomplete', 'off');
    // Подсказки с задержкой: запрос на каждую букву — лишняя нагрузка
    // и на свой сервер, и на СДЭК.
    cityInput.addEventListener('input', () => {
      clearTimeout(suggestTimer);
      const value = cityInput.value;
      suggestTimer = setTimeout(() => suggestCities(value), 300);
    });
  }

  // Улица: ищем после паузы в наборе. Список пунктов уже на нашем сервере,
  // так что каждый такой поиск — обращение к памяти, а не к СДЭК.
  const street = q('[data-delivery-street]');
  if (street) {
    let streetTimer = null;
    street.addEventListener('input', () => {
      clearTimeout(streetTimer);
      streetTimer = setTimeout(openPvzList, 400);
    });
    street.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); openPvzList(); }
    });
  }

  const dates = q('[data-delivery-dates]');
  if (dates) {
    dates.addEventListener('change', () => {
      cart.setShipping({ date: dates.value });
      onChange();
    });
  }

  recalc();
}
